import React, { useEffect, useState } from "react";
import { View, Text, Image, ImageSourcePropType, StyleSheet, Pressable } from "react-native";
import { BG_STYLES, BORDER_STYLES, BottomDrawer, Button, BUTTON_STATUS, COMMON_STYLES, DefaultProductImage, Input, LazyImage, RadioButton, SIZE, TEXT_STYLES, TYPOGRAPHY, VARIANTS_STYLES } from "@dehaat/dds";
import { ProductCatalogListing } from "app/models/RequestFlow";
import OutlinedInput from "./OutlinedInput";

type ChangePriceDrawerProps = {
  visible: boolean;
  onClose: () => void;
  selectedProduct: ProductCatalogListing | null;
  newPrice: string;
  setNewPrice: (price: string) => void;
  handleSubmit: (product: ProductCatalogListing , reason: string) => void;
};

const ChangePriceDrawer = ({
  visible,
  selectedProduct,
  newPrice,
  setNewPrice,
  handleSubmit,
  onClose,
}: ChangePriceDrawerProps) => {
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");
  const canSubmit = !error && !!newPrice;
  const handleChange = (value: string) => {
    const numericOnly = value.replace(/[^0-9]/g, "");
    setNewPrice(numericOnly);
  };

  useEffect(() => {
    if (newPrice !== "" && Number(newPrice) === 0) {
      setError("Please enter an amount greater than zero");
    } else if (
      selectedProduct &&
      Number(newPrice) === Number(selectedProduct.online_price)
    ) {
      setError("New price cannot be same as current selling price");
    } else {
      setError("");
    }
  }, [newPrice, selectedProduct]);
  return (
      <BottomDrawer
            visible={visible}
            onClose={onClose}
            heading="Change Price"
            closeOnBackdropPress
          >
            <View style={[COMMON_STYLES.row, { columnGap: 8 }]}>
              <View style={styles.ImageWrapper}>
                <LazyImage
                  imageUrl={selectedProduct?.product_image}
                  fallBackIcon={
                    <Image
                      source={DefaultProductImage as ImageSourcePropType}
                      style={{ width: 85, height: 85 }}
                    />
                  }
                  imgStyles={{ width: 85, height: 85 }}
                />
              </View>
              <View style={{ rowGap: 4 }}>
                <Text style={styles.brand}>{selectedProduct?.brand}</Text>
                <Text style={styles.name}>{selectedProduct?.product_name}</Text>
                <Text style={styles.name}>{selectedProduct?.attributes_name}</Text>
              </View>
            </View>
            <View
              style={[
                COMMON_STYLES.p1,
                COMMON_STYLES.mt1,
                BG_STYLES.bgNeutral10,
                COMMON_STYLES.br1,
                styles.sourceContainer,
              ]}
            >
              <Text style={styles.source}>Source</Text>
              <View style={[COMMON_STYLES.row]}>
                <Pressable
                  style={[
                    styles.sourceType,
                    BG_STYLES.bgNeutral90
                  ]}
                >
                  <Text
                    style={[
                      TYPOGRAPHY.CAPTION1,
                      VARIANTS_STYLES.MEDIUM,
                      TEXT_STYLES.textNeutral00
                    ]}
                  >
                    Store
                  </Text>
                </Pressable>
              </View>
              <Text style={[styles.source, COMMON_STYLES.pv1]}>
                Current Selling Price:
                <Text style={styles.currentPrice}>
                  {' '}
                  ₹{selectedProduct?.online_price}{' '}
                </Text>
              </Text>
              <Input
                containerStyle={[
                  BG_STYLES.bgNeutral10,
                  COMMON_STYLES.b1,
                  BORDER_STYLES.borderPrimary100,
                  COMMON_STYLES.p1,
                ]}
                label={`Price for${selectedProduct?.attributes_name}`}
                variant="outlined"
                onChangeText={handleChange}
                value={newPrice}
                keyboardType="numeric"
              />
              <Text style={styles.suggested}>
                Threshold Price:
                <Text style={[VARIANTS_STYLES.SEMI_EMPHASIZED]}>
                  ₹{selectedProduct?.suggested_price}
                </Text>
              </Text>
              {error ? <Text style={[styles.errorText,TYPOGRAPHY.TEXT2,VARIANTS_STYLES.MEDIUM,TEXT_STYLES.textError100]}>{error}</Text> : null}
            </View>
            <View style={COMMON_STYLES["mt3/4"]}>
        <Text
          style={[
            TYPOGRAPHY.TEXT2,
            VARIANTS_STYLES.MEDIUM,
            TEXT_STYLES.textNeutral90,
            COMMON_STYLES.mb1,
          ]}
        >
          Reason for new request
        </Text>

        <View
          style={[COMMON_STYLES.row, {
            gap: 8,
          }]}
        >
          <RadioButton
            list={[
              {
                radioElement: <View />,
                value: "reason",
              },
            ]}
            selected={0}
            onClick={() => { }}
            containerStyle={{}}
            radioTextContainerStyleObj={[COMMON_STYLES.p0, { margin: 0 }]}
          />
          <OutlinedInput
            label="Write reason"
            placeholder="Reason here..."
            multiline
            value={reason}
            onChangeText={setReason}
            containerStyle={COMMON_STYLES.flex1}
          />
        </View>
        </View>
            <Button
              size={SIZE.large}
             onClick={() => 
                canSubmit ? (selectedProduct && handleSubmit(selectedProduct,reason)) : null
              }
              text={'Submit Request'}
              buttonStyle={COMMON_STYLES.mt1}
              status={canSubmit ? BUTTON_STATUS.ACTIVE : BUTTON_STATUS.DISABLED}
            />
          </BottomDrawer>
  )
}

export default ChangePriceDrawer

const styles = StyleSheet.create({

  brand: {
    ...TEXT_STYLES.textNeutral70,
    ...TYPOGRAPHY.CAPTION1,
    ...VARIANTS_STYLES.MEDIUM,
  },
  name: {
    ...TEXT_STYLES.textNeutral90,
    ...TYPOGRAPHY.TEXT1,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
  },
  suggested: {
    ...TYPOGRAPHY.CAPTION1,
    ...TEXT_STYLES.textNeutral70,
  },
    source: {
    ...TYPOGRAPHY.TEXT2,
    ...VARIANTS_STYLES.MEDIUM,
    ...TEXT_STYLES.textNeutral100,
  },
  ImageWrapper: {
    ...COMMON_STYLES['br1/2'],
    ...COMMON_STYLES['p0.5'],
    ...COMMON_STYLES.b1,
    ...BORDER_STYLES.borderNeutral20,
  },
  sourceType: {
    ...COMMON_STYLES['br1/2'],
    ...COMMON_STYLES.b1,
    ...COMMON_STYLES.alignItemsCenter,
    ...COMMON_STYLES.justifyCenter,
    ...COMMON_STYLES.ph1,
    ...COMMON_STYLES['pv1/2'],
    ...COMMON_STYLES['mr0.5'],
  },
  sourceContainer: { 
    gap: 10 
  },
currentPrice: {
    ...TYPOGRAPHY.TEXT1,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
    ...TEXT_STYLES.textNeutral90,
  },
  errorText: {
    marginTop: 6,
  },
});
